import type Konva from "konva";
import { useCallback, useEffect, useRef, useState } from "react";
import type { DraftElement, DrawStyle, DrawTool, Shape, ShapeKind, Stroke } from "./types.js";

/**
 * Controller for drawing tools (Pen / Line / Arrow / Rect / Circle / Lasso).
 *
 * It listens to stage pointer events and produces:
 *   - a `draft` element shown live while the user drags,
 *   - a `lassoPath` while the user lassos,
 *   - calls back on commit (pointer up) so the caller can persist the result.
 *
 * The hook is stage-agnostic — caller hands in the current stage via the
 * Konva event objects; pointer positions are converted to board coordinates
 * through the stage's absolute transform.
 */
export interface DrawingApi {
  draft: DraftElement | null;
  lassoPath: number[] | null;
  onPointerDown: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  onPointerMove: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  onPointerUp: (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => void;
  /**
   * Discard the in-flight draft without committing — used when a two-finger
   * pinch/pan gesture begins after the first finger already started a stroke.
   */
  cancel: () => void;
}

interface UseDrawingArgs {
  tool: DrawTool | null;
  style: DrawStyle;
  onCommitStroke: (stroke: Stroke) => void;
  onCommitShape: (shape: Shape) => void;
  onCommitLasso: (path: number[]) => void;
}

const SHAPE_OF: Partial<Record<DrawTool, ShapeKind>> = {
  line: "line",
  arrow: "arrow",
  rect: "rect",
  circle: "circle",
};

export function useDrawing({
  tool,
  style,
  onCommitStroke,
  onCommitShape,
  onCommitLasso,
}: UseDrawingArgs): DrawingApi {
  const [draft, setDraft] = useState<DraftElement | null>(null);
  const [lassoPath, setLassoPath] = useState<number[] | null>(null);
  // Mirror state into refs so onPointerUp can read the latest values
  // without invoking setState reducers (which must stay pure — calling
  // commit handlers from inside them caused double-commits + duplicate
  // keys under React 19 StrictMode).
  const draftRef = useRef<DraftElement | null>(null);
  const lassoRef = useRef<number[] | null>(null);
  useEffect(() => {
    draftRef.current = draft;
  }, [draft]);
  useEffect(() => {
    lassoRef.current = lassoPath;
  }, [lassoPath]);
  const startBoard = useRef<{ x: number; y: number } | null>(null);
  const pointerId = useRef<number | null>(null);

  const boardPoint = (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
    const stage = e.target.getStage();
    if (!stage) return null;
    const pos = stage.getPointerPosition();
    if (!pos) return null;
    const tr = stage.getAbsoluteTransform().copy().invert();
    return tr.point(pos);
  };

  const onPointerDown = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!tool) return;
      const stage = e.target.getStage();
      if (!stage) return;
      // Only start a stroke when the user pressed on empty stage/pitch layer.
      if (e.target !== stage && e.target.getType() !== "Layer") return;
      const pt = boardPoint(e);
      if (!pt) return;
      const evt = e.evt as PointerEvent;
      pointerId.current = evt.pointerId ?? null;
      startBoard.current = pt;

      if (tool === "pen") {
        setDraft({
          kind: "stroke",
          stroke: {
            id: crypto.randomUUID(),
            color: style.color,
            width: style.width,
            dashed: style.dashed,
            points: [pt.x, pt.y],
          },
        });
      } else if (tool === "lasso") {
        setLassoPath([pt.x, pt.y]);
      } else {
        const shapeKind = SHAPE_OF[tool];
        if (!shapeKind) return;
        setDraft({
          kind: "shape",
          shape: {
            id: crypto.randomUUID(),
            kind: shapeKind,
            color: style.color,
            width: style.width,
            dashed: style.dashed,
            x: pt.x,
            y: pt.y,
            w: 0,
            h: 0,
          },
        });
      }
    },
    [tool, style.color, style.width, style.dashed],
  );

  const onPointerMove = useCallback(
    (e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!tool) return;
      if (!startBoard.current) return;
      const pt = boardPoint(e);
      if (!pt) return;

      if (tool === "pen") {
        setDraft((prev) => {
          if (!prev || prev.kind !== "stroke") return prev;
          return {
            kind: "stroke",
            stroke: {
              ...prev.stroke,
              points: [...prev.stroke.points, pt.x, pt.y],
            },
          };
        });
      } else if (tool === "lasso") {
        setLassoPath((prev) => (prev ? [...prev, pt.x, pt.y] : [pt.x, pt.y]));
      } else {
        // Capture the ref's current value BEFORE the reducer runs. React 19
        // StrictMode invokes state reducers twice in dev, and the second
        // invocation can fire after onPointerUp has already nulled
        // `startBoard.current` — reading it inside the reducer then throws
        // `Cannot read properties of null (reading 'x')`.
        const s = startBoard.current;
        if (!s) return;
        setDraft((prev) => {
          if (!prev || prev.kind !== "shape") return prev;
          if (prev.shape.kind === "circle") {
            const dx = pt.x - s.x;
            const dy = pt.y - s.y;
            const r = Math.hypot(dx, dy);
            return {
              kind: "shape",
              shape: { ...prev.shape, x: s.x, y: s.y, w: r, h: r },
            };
          }
          return {
            kind: "shape",
            shape: { ...prev.shape, w: pt.x - s.x, h: pt.y - s.y },
          };
        });
      }
    },
    [tool],
  );

  const onPointerUp = useCallback(
    (_e: Konva.KonvaEventObject<MouseEvent | TouchEvent>) => {
      if (!tool) return;
      pointerId.current = null;
      startBoard.current = null;

      // ⚠ Do NOT call commit handlers inside setState reducers. React 19
      // StrictMode invokes reducers twice in dev → double commits → "two
      // children with the same key" warnings + duplicate persisted shapes.
      // We mirror draft/lasso state into refs (above) so we can read the
      // committed values here without abusing setState as a getter.
      const current = draftRef.current;
      const currentLasso = lassoRef.current;

      if (tool === "pen") {
        if (current?.kind === "stroke" && current.stroke.points.length >= 4) {
          onCommitStroke(current.stroke);
        }
        setDraft(null);
        return;
      }
      if (tool === "lasso") {
        if (currentLasso && currentLasso.length >= 6) {
          onCommitLasso(currentLasso);
        }
        setLassoPath(null);
        return;
      }
      if (current?.kind === "shape") {
        const sh = current.shape;
        const meaningful =
          sh.kind === "circle" ? sh.w > 4 : Math.abs(sh.w) > 4 || Math.abs(sh.h) > 4;
        if (meaningful) {
          // normalize rects to positive w/h
          let out = sh;
          if (sh.kind === "rect") {
            out = {
              ...sh,
              x: sh.w < 0 ? sh.x + sh.w : sh.x,
              y: sh.h < 0 ? sh.y + sh.h : sh.y,
              w: Math.abs(sh.w),
              h: Math.abs(sh.h),
            };
          }
          onCommitShape(out);
        }
      }
      setDraft(null);
    },
    [tool, onCommitStroke, onCommitShape, onCommitLasso],
  );

  const cancel = useCallback(() => {
    setDraft(null);
    setLassoPath(null);
    draftRef.current = null;
    lassoRef.current = null;
    startBoard.current = null;
    pointerId.current = null;
  }, []);

  return { draft, lassoPath, onPointerDown, onPointerMove, onPointerUp, cancel };
}

/**
 * Ray-casting point-in-polygon test. `polygon` is a flat `[x0,y0,x1,y1,…]`
 * coordinate list (a completed lasso path). Callers use it to hit-test scene
 * elements against the lasso selection region.
 */
export function pointInPolygon(x: number, y: number, polygon: number[]): boolean {
  const n = Math.floor(polygon.length / 2);
  let inside = false;
  for (let i = 0, j = n - 1; i < n; j = i++) {
    const xi = polygon[i * 2] as number;
    const yi = polygon[i * 2 + 1] as number;
    const xj = polygon[j * 2] as number;
    const yj = polygon[j * 2 + 1] as number;
    const intersect = yi > y !== yj > y && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi;
    if (intersect) inside = !inside;
  }
  return inside;
}
